"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Button } from "./Button";

function FloatingPaths({ position }) {
  const paths = Array.from({ length: 36 }, (_, i) => ({
    id: i,
    d: `M-${380 - i * 5 * position} -${189 + i * 6}C-${380 - i * 5 * position
      } -${189 + i * 6} -${312 - i * 5 * position} ${216 - i * 6} ${152 - i * 5 * position
      } ${343 - i * 6}C${616 - i * 5 * position} ${470 - i * 6} ${684 - i * 5 * position
      } ${875 - i * 6} ${684 - i * 5 * position} ${875 - i * 6}`,
    color: `rgba(15,23,42,${0.1 + i * 0.03})`,
    width: 0.5 + i * 0.03,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none">
      <svg
        className="w-full h-full text-slate-950 dark:text-white"
        viewBox="0 0 696 316"
        fill="none"
      >
        <title>Background Paths</title>
        {paths?.map((path) => (
          <motion.path
            key={path?.id}
            d={path?.d}
            stroke="currentColor"
            strokeWidth={path?.width}
            strokeOpacity={0.1 + path?.id * 0.03}
            initial={{ pathLength: 0.3, opacity: 0.6 }}
            animate={{
              pathLength: 1,
              opacity: [0.3, 0.6, 0.3],
              pathOffset: [0, 1, 0],
            }}
            transition={{
              duration: 20 + Math.random() * 10,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
          />
        ))}
      </svg>
    </div>
  );
}

function ImmersiveTextAnimation() {
  const [textPhase, setTextPhase] = useState("gradone"); // gradone -> oneday -> dayone

  useEffect(() => {
    // Show "GradOne" for 0.4 seconds
    const timer1 = setTimeout(() => {
      setTextPhase("oneday");
    }, 400);

    // Transition from "One day" to "Day One" after 0.8 seconds total
    const timer2 = setTimeout(() => {
      setTextPhase("dayone");
    }, 800);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <div className="text-5xl sm:text-7xl md:text-8xl font-bold mb-8 tracking-tighter min-h-[120px] flex flex-col items-center justify-center">
      {/* GradOne - Always visible */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.3,
          ease: [0.43, 0.13, 0.23, 0.96],
        }}
        className="text-transparent bg-clip-text bg-gradient-to-r from-neutral-900 to-neutral-700/80 dark:from-white dark:to-white/80"
      >
        GradOne
      </motion.div>
      {/* Second line animation: "One day" -> "Day One" with pink-to-yellow gradient */}
      <AnimatePresence mode="wait">
        {textPhase === "oneday" && (
          <motion.div
            key="oneday"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -30, opacity: 0 }}
            transition={{
              duration: 0.25,
              ease: "easeInOut",
            }}
            className="text-4xl sm:text-5xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-[#ff69b4] to-[#ffb347]"
          >
            One day
          </motion.div>
        )}
        {textPhase === "dayone" && (
          <motion.div
            key="dayone"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              duration: 0.25,
              ease: "easeInOut",
            }}
            className="text-4xl sm:text-5xl md:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-[#ff69b4] to-[#ffb347]"
          >
            Day One
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function BackgroundPaths({
  title = "Background Paths",
  isTransitioning = false,
}) {
  return (
    <AnimatePresence>
      <motion.div
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-white dark:bg-neutral-950"
        animate={{
          opacity: isTransitioning ? 0 : 1,
          scale: isTransitioning ? 0.95 : 1,
        }}
        transition={{
          duration: 0.5,
          ease: [0.43, 0.13, 0.23, 0.96],
        }}
      >
        <div className="absolute inset-0">
          <FloatingPaths position={1} />
          <FloatingPaths position={-1} />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: isTransitioning ? 0 : 1,
              y: isTransitioning ? -20 : 0,
            }}
            transition={{
              duration: isTransitioning ? 0.6 : 2,
              ease: "easeInOut",
            }}
            className="max-w-4xl mx-auto"
          >
            <ImmersiveTextAnimation />

            <motion.div
              className="inline-block group relative bg-gradient-to-b from-black/10 to-white/10 
              dark:from-white/10 dark:to-black/10 p-px rounded-2xl backdrop-blur-lg 
              overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
              animate={{
                opacity: isTransitioning ? 0 : 1,
                scale: isTransitioning ? 0.9 : 1,
              }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              <Button
                variant="default"
                className="rounded-[1.15rem] px-8 py-6 text-lg font-bold shadow-xl 
                transition-all duration-300 group-hover:-translate-y-1 hover:shadow-2xl"
              >
                <span className="opacity-90 group-hover:opacity-100 transition-opacity">
                  Discover Excellence
                </span>
                <span
                  className="ml-3 opacity-70 group-hover:opacity-100 group-hover:translate-x-1.5 transition-all duration-300"
                >
                  →
                </span>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}